import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiCheck, FiX, FiAlertCircle, FiRefreshCw } = FiIcons;

const CookieConsent = ({ onAccept }) => {
  const [status, setStatus] = useState('pending'); // pending, accepted, refused
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent-choice');
    const timestamp = localStorage.getItem('cookie-consent-timestamp');
    
    // On ne restaure QUE si c'est ACCEPTÉ.
    // Le refus ne doit pas être persistant pour permettre de changer d'avis au rechargement (F5).
    if (consent === 'accepted' && timestamp) {
      const now = new Date().getTime();
      const oneDay = 24 * 60 * 60 * 1000;
      
      if (now - parseInt(timestamp) < oneDay) {
        setStatus('accepted');
        onAccept();
        return;
      }
    }
    
    // Sinon, on affiche la bannière (même si c'était refusé avant, le refresh reset le refus)
    const timer = setTimeout(() => setIsVisible(true), 1500);
    return () => clearTimeout(timer);
  }, [onAccept]);

  const handleChoice = (choice) => {
    const now = new Date().getTime();
    
    if (choice === 'accepted') {
      // On sauvegarde UNIQUEMENT l'acceptation pour 24h
      localStorage.setItem('cookie-consent-choice', 'accepted');
      localStorage.setItem('cookie-consent-timestamp', now.toString());
      onAccept();
    } else {
      // En cas de refus, on NE SAUVEGARDE PAS dans le localStorage.
      // Ainsi, au prochain rafraîchissement (F5), la bannière réapparaîtra.
      // On nettoie même par sécurité.
      localStorage.removeItem('cookie-consent-choice');
      localStorage.removeItem('cookie-consent-timestamp');
    }
    
    setStatus(choice);
    setIsVisible(false);
  };

  const handleRefresh = () => {
    // Force le nettoyage avant de recharger pour sortir de la boucle si nécessaire
    localStorage.removeItem('cookie-consent-choice');
    localStorage.removeItem('cookie-consent-timestamp');
    window.location.reload();
  };

  return (
    <>
      {/* 1. La Bannière Initiale (Non-intrusive au début) */}
      <AnimatePresence>
        {isVisible && status === 'pending' && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-6 left-6 right-6 md:left-auto md:right-8 md:w-[400px] z-[200]"
          >
            <div className="bg-white rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-gray-100 p-6 overflow-hidden relative">
              <div className="absolute top-0 left-0 w-2 h-full bg-[#00a48c]" />
              
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#f0fcf9] text-[#00a48c] flex items-center justify-center shrink-0">
                  <SafeIcon icon={FiShield} size={20} />
                </div>
                <div>
                  <h4 className="font-black text-[#004d44] text-sm uppercase tracking-tight">Respect de votre vie privée</h4>
                  <p className="text-[11px] text-gray-500 font-medium leading-relaxed mt-1">
                    Nous utilisons des cookies pour analyser notre audience (Google Analytics) et améliorer votre expérience. L'accès au simulateur nécessite votre accord.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => handleChoice('accepted')}
                  className="flex-1 bg-[#00a48c] hover:bg-[#00856f] text-white py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#00a48c]/20"
                >
                  <SafeIcon icon={FiCheck} /> Accepter tout
                </button>
                <button
                  onClick={() => handleChoice('refused')}
                  className="px-4 py-3 rounded-xl font-bold text-[10px] text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all uppercase tracking-widest"
                >
                  Refuser
                </button>
              </div>
              
              <div className="mt-4 text-center">
                <a href="/politique-confidentialite" className="text-[9px] font-bold text-gray-300 hover:text-[#00a48c] uppercase tracking-tighter transition-colors">En savoir plus sur nos cookies</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 2. Le Bloqueur d'accès (Si refusé) */}
      <AnimatePresence>
        {status === 'refused' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-[300] bg-white/80 backdrop-blur-xl flex items-center justify-center p-6 text-center"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="max-w-md"
            >
              <div className="w-20 h-20 rounded-3xl bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-8 shadow-inner">
                <SafeIcon icon={FiAlertCircle} size={40} />
              </div>
              <h2 className="text-3xl font-black text-[#004d44] mb-4 uppercase tracking-tight">Accès restreint</h2>
              <p className="text-gray-500 font-medium mb-8 leading-relaxed">
                Désolé, l'utilisation de notre simulateur de rendement PER et l'accès à nos services conseils nécessitent l'acceptation des cookies de mesure d'audience.
              </p>
              <div className="flex flex-col gap-4">
                <button
                  onClick={handleRefresh}
                  className="bg-[#00a48c] text-white py-4 px-8 rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-[#00a48c]/20 hover:scale-105 transition-all flex items-center justify-center gap-3"
                >
                  <SafeIcon icon={FiRefreshCw} /> Revoir mes préférences
                </button>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Le site sera à nouveau accessible dès acceptation</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieConsent;